import { sync, get, call } from 'vuex-pathify';
import { Draw } from 'ol/interaction';
import { GeoJSON, Polyline } from 'ol/format';
import { Circle, Fill, Stroke, Style } from 'ol/style';
import { getContext } from '@/assets/js/mapContext';
import booleanIntersects from '@turf/boolean-intersects';
import FlowLine from 'ol-ext/style/FlowLine';

export default {
  computed: {
    isSketchActive: sync('tools/toolStatus@isSketchActive'),
    activeTool: sync('tools/activeTool'),
    currentProjectId: get('layers/project@id'),
  },
  methods: {
    createProjectSketch: call('sketch/createProjectSketch'),
    getLineGeometry(features) {
      const geometry = features.map(feature => {
        const olGeometry = new Polyline({ factor: 1e6 }).readGeometry(feature.geom, {
          dataProjection: 'EPSG:4326',
          featureProjection: 'EPSG:3857',
        });
        const convertedGeometry = new GeoJSON().writeGeometryObject(olGeometry);
        return {
          type: 'Feature',
          geometry: convertedGeometry,
          properties: {
            id: feature.id,
            color: feature.color,
            stroke: feature.width,
            authorId: feature.added_by,
            authorName: feature.added_by_name,
            created: feature.added_at,
          },
        };
      });
      return {
        type: 'FeatureCollection',
        features: geometry ?? [],
        crs: {
          type: 'name',
          properties: {
            name: this.$_config.defaultEpsg,
          },
        },
      };
    },
    getSketchStyle(parameters) {
      const { color, stroke } = parameters;
      return new Style({
        stroke: new Stroke({
          color,
          width: stroke,
        }),
        image: new Circle({
          radius: stroke + 2,
          fill: new Fill({
            color,
          }),
        }),
      });
    },
    toggleSketchLayer(features) {
      if (!features) return this.addCustomGeojsonLayer({ id: 'sketches' });

      /** Since user can only draw with freehand mode for now, only polyline linestrings are supported  */
      const collection = this.getLineGeometry(features);

      this.addCustomGeojsonLayer({
        id: 'sketches',
        geometry: collection ?? undefined,
        layerParams: {
          renderImage: true,
          zIndex: 2000,
          style: ft => {
            const featureProperties = ft.getProperties();
            return new Style({
              stroke: new Stroke({
                color: featureProperties?.color ?? '#000',
                width: featureProperties?.stroke ?? 2,
              }),
            });
          },
        },
      });
    },
    changeSketchStyle(styleParameters, createAction) {
      const sketchInteraction = this.getInteractionByName('sketching');
      if (!sketchInteraction) return;
      this.turnOnSketching(styleParameters, createAction);
    },

    /** Sketch drawing interaction */
    async toggleSketching(value, styleParameters, createAction) {
      if (value && this.activeTool !== 'sketch') {
        getContext(this).$emit('deactivateAllTools');
      } else if (!value && this.activeTool === 'sketch') {
        this.deactivateToolHandler('sketch');
      }
      await this.$nextTick();
      if (!value) return this.turnOffSketching();
      this.turnOnSketching(styleParameters, createAction);
      this.activeTool = 'sketch';
    },
    turnOnSketching(styleParameters, createAction) {
      this.turnOffSketching();
      const sketchingInteraction = new Draw({
        source: this.getLayerById('sketches')?.getSource() ?? undefined,
        type: 'LineString',
        freehand: true,
        style: this.getSketchStyle(styleParameters),
      });
      sketchingInteraction.set('name', 'sketching');
      this.map.addInteraction(sketchingInteraction);
      sketchingInteraction.on('drawend', $event => this.onSketchEnd($event, styleParameters, createAction));
    },
    turnOffSketching() {
      this.map.removeInteraction(this.getInteractionByName('sketching'));
    },
    async onSketchEnd(drawEvent, styleParameters, createAction) {
      const feature = drawEvent.feature;
      feature.setProperties(styleParameters);
      const featureObject = new GeoJSON().writeFeatureObject(feature);
      const featureGeojson = {
        ...featureObject,
        crs: {
          type: 'name',
          properties: {
            name: this.$_config.defaultEpsg,
          },
        },
      };
      try {
        const r = await createAction({
          color: styleParameters.color,
          width: styleParameters.stroke,
          feature: featureGeojson,
        });
        this.toggleSketchLayer(r);
      } catch (error) {
        /** Remove drawn feature on error - it should be no longer on the map */
        this.getLayerByIdEmit({
          id: 'sketches',
          callback: layer => layer.getSource().removeFeature(drawEvent.feature),
        });
        console.error(error);
      }
    },

    /** IDENTIFICATION */
    async toggleSketchIdentification(value = !this.isSketchIdentificationActive, callback) {
      value ? getContext(this).$emit('deactivateAllTools') : this.deactivateToolHandler('sketchIdentification');
      this.isSketchIdentificationActive = value;
      this.dettachCursorMoveHandler();
      await this.$nextTick();
      if (value) {
        this.isIdentificationToolActive = false;
        this.activeTool = 'sketchIdentification';
        this.attachListener(
          'click',
          this.getIdentificationCallback({
            identifyCallback: callback,
            specialType: 'sketch',
          }),
          {
            type: 'on',
            cursor: null,
          }
        );
        this.attachMoveCursorHandler('pointer', layer => layer.get('id') === 'sketches', true);
      }
    },
    identifySketchFeatureInfo(identifyCallback) {
      return e => {
        let detectedFeature;
        this.map.forEachFeatureAtPixel(
          e.pixel,
          feature => {
            detectedFeature = feature.getProperties();
            return true;
          },
          {
            hitTolerance: 5,
            layerFilter: layer => layer.get('id') === 'sketches',
          }
        );
        delete detectedFeature?.geometry;
        identifyCallback({
          coordinates: e.coordinate,
          feature: detectedFeature,
        });
      };
    },

    /** ERASE */
    async toggleSketchErase(value, callback) {
      if (value && this.activeTool !== 'sketchErase') {
        getContext(this).$emit('deactivateAllTools');
      } else if (!value && this.activeTool === 'sketchErase') {
        this.deactivateToolHandler('sketchErase');
      }
      await this.$nextTick();
      if (!value) return this.turnOffSketchErasing();
      this.turnOnSketchErasing(callback);
      this.activeTool = 'sketchErase';
    },
    turnOffSketchErasing() {
      this.map.removeInteraction(this.getInteractionByName('sketchErasing'));
    },
    turnOnSketchErasing(callback) {
      this.turnOffSketchErasing();
      const erasingInteraction = new Draw({
        type: 'LineString',
        freehand: true,
        style: [
          new FlowLine({
            width: 0,
            width2: 8,
            color: 'rgba(209, 213, 219, 0)',
            color2: 'rgba(209, 213, 219, 0.8)',
            lineCap: 'round',
          }),
          new Style({
            zIndex: 2,
            image: new Circle({
              radius: 6,
              fill: new Fill({
                color: 'rgba(255, 255, 255, 0.8)',
              }),
              stroke: new Stroke({
                color: 'black',
                width: 2,
              }),
            }),
          }),
        ],
      });
      erasingInteraction.set('name', 'sketchErasing');
      this.map.addInteraction(erasingInteraction);
      erasingInteraction.on('drawend', $event => this.onSketchEraseEnd($event, callback));
    },
    async onSketchEraseEnd(event, callback = () => {}) {
      const feature = event.feature;
      const featureGeometry = new GeoJSON().writeGeometryObject(feature.getGeometry());
      let featuresToRemove = [];
      this.getLayerById('sketches')
        ?.getSource()
        .forEachFeatureIntersectingExtent(feature.getGeometry().getExtent(), ft => {
          const geometry = new GeoJSON().writeGeometryObject(ft.getGeometry());
          const isIntersecting = booleanIntersects(featureGeometry, geometry);
          if (isIntersecting) featuresToRemove.push(ft.get('id'));
        });
      callback(featuresToRemove);
    },
  },
  mounted() {
    getContext(this).$off('sketch-action');
    getContext(this).$on('sketch-action', this.toggleSketching);

    getContext(this).$off('sketchErase-action');
    getContext(this).$on('sketchErase-action', this.toggleSketchErase);

    getContext(this).$off('sketchIdentification-action');
    getContext(this).$on('sketchIdentification-action', this.toggleSketchIdentification);

    getContext(this).$off('toggleSketchLayer');
    getContext(this).$on('toggleSketchLayer', this.toggleSketchLayer);

    getContext(this).$off('changeSketchStyle');
    getContext(this).$on('changeSketchStyle', this.changeSketchStyle);
  },
};
